/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Monsieur Croissant — types and endpoints.
 *
 * Its own module rather than lines appended to api/types.ts and api/endpoints.ts.
 * The block union is the biggest type in the client and it changes on its own
 * schedule; keeping it here means a change to the assistant never produces a
 * diff in the file that describes recipes.
 *
 * Mirrors server/chef_blocks.ts. The two must agree; the server's version is
 * the one that is authoritative.
 */

import { api } from "./client.ts";

// ─────────────────────────────────────────────────────────────────────────────
// Blocks
// ─────────────────────────────────────────────────────────────────────────────

export interface ChefIdea {
  title: string;
  why: string;
  recipeId?: string | null;
  minutes?: number | null;
  tags?: string[];
}

export interface ChefIngredientLine {
  groupLabel?: string | null;
  displayName: string;
  quantity?: number | null;
  quantityMax?: number | null;
  unit?: string | null;
  prepNote?: string | null;
  isOptional?: boolean;
}

export interface ChefStepLine {
  text: string;
  durationMinutes?: number | null;
  groupLabel?: string | null;
}

export type AllergenStatus = "clear" | "unverified" | "conflict";

/**
 * The per-serving panel on a chat recipe card. Mirrors ChefNutrition in
 * server/chef_blocks.ts.
 *
 * Values arrive PRE-FORMATTED ("18.2 g", "890 mg"). Do not reformat them and do
 * not do arithmetic on them — the server owns the rounding so that this card
 * and the iOS one cannot disagree about the same recipe.
 */
export interface ChefNutrition {
  items: Array<{ key: string; label: string; value: string }>;
  provenance: "estimated" | "published";
  confidence: "high" | "medium" | "low";
  note?: string | null;
}

export interface ChefRecipeBlock {
  type: "recipe";
  draftId?: string | null;
  recipeId?: string | null;
  title: string;
  description?: string | null;
  servings: number;
  prepMinutes?: number | null;
  cookMinutes?: number | null;
  cuisine?: string | null;
  course?: string | null;
  difficulty?: string | null;
  dietTags: string[];
  tags: string[];
  ingredients: ChefIngredientLine[];
  steps: ChefStepLine[];
  notes?: string | null;
  allergenStatus: AllergenStatus;
  allergenDetail?: string | null;
  /**
   * Absent — the key itself, not just the value — when there is no estimate
   * yet and when the household is nutrition-silent. Those two cases are
   * deliberately indistinguishable here: "nutrition unavailable" is still a
   * sentence about calories on a screen that should not have one.
   */
  nutrition?: ChefNutrition | null;
  model?: string | null;
  canExportPdf: boolean;
}

export type ChefBlock =
  | { type: "text"; markdown: string }
  | { type: "ideas"; items: ChefIdea[] }
  | ChefRecipeBlock
  | {
      type: "recipeRefs";
      caption?: string | null;
      items: Array<{
        recipeId: string; title: string; minutes?: number | null;
        tags?: string[]; lastCooked?: string | null;
      }>;
    }
  | {
      type: "plan";
      from: string; to: string;
      days: Array<{
        date: string; weekday: string;
        entries: Array<{ slot: string; title: string; servings?: number | null }>;
      }>;
    }
  | {
      type: "shopping";
      listId?: string | null; title: string;
      items: Array<{
        name: string; quantity?: number | null; unit?: string | null;
        aisle?: string | null; checked?: boolean;
      }>;
    }
  | {
      type: "substitutions";
      forName: string;
      options: Array<{ toName: string; ratioNote: string; confidence?: number | null }>;
    }
  | { type: "table"; caption?: string | null; columns: string[]; rows: string[][] }
  /**
   * Where a web answer came from. Never optional when a search ran — an answer
   * about a recall or a health claim that doesn't say who said it can't be
   * weighed, and the assistant has no standing to be believed on its own.
   */
  | {
      type: "sources";
      items: Array<{ title: string; url: string }>;
      searchedOn: string;
    }
  | { type: "notice"; tone: "info" | "warn" | "danger"; title: string; body: string }
  /**
   * What a dish touches, medically. Flags, never a verdict.
   *
   * `kind` reaches the client because 'consistency' reads the OPPOSITE way to
   * 'avoid' — warfarin and greens is "keep it steady" — and a renderer that
   * collapsed the two would tell someone to stop eating vegetables.
   */
  | {
      type: "medicalFlags";
      items: Array<{
        severity: "warn" | "advisory";
        kind: "avoid" | "limit" | "consistency" | "timing" | "prefer";
        title: string;
        people: string[];
        matched: string[];
        guidance: string;
      }>;
      disclaimer: string;
    }
  | {
      type: "proposal";
      proposalId: string;
      kind: "plan.add" | "shopping.add" | "recipe.save";
      summary: string;
      detail: string[];
      confirmLabel: string;
      status: "pending" | "applied" | "dismissed" | "expired";
    }
  | { type: "chips"; items: Array<{ label: string; send: string }> };

export interface ChefMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  blocks: ChefBlock[];
  model?: string | null;
  createdAt: string;
  /** Set locally on a message that hasn't reached the server yet. */
  pending?: boolean;
}

export interface ChefConversation {
  id: string;
  title: string;
  createdAt: string;
  messages: ChefMessage[];
  starters: Extract<ChefBlock, { type: "chips" }> | null;
}

/** One selectable model, described for the person choosing it. */
export interface ModelChoice {
  id: string;
  label: string;
  blurb: string;
  isDefault: boolean;
}

/**
 * The pickers, as data from the server.
 *
 * Deliberately not a hardcoded list here: adding a model should be a server
 * deploy, not a web build plus an App Store review, and the two clients cannot
 * drift from each other or from what the server will accept.
 */
export interface ModelSettings {
  recipe: { choices: ModelChoice[]; selected: string; isDefault: boolean };
  chat: { choices: ModelChoice[]; selected: string; isDefault: boolean };
  webSearchAvailable: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Medical considerations
// ─────────────────────────────────────────────────────────────────────────────

export interface MedicalConditionOption {
  key: string;
  label_en: string;
  category: string;
  summary_en: string;
  severity: "warn" | "advisory";
  /** Pregnancy and similar — the editor asks for an end date. */
  is_time_boxed: boolean;
}

export interface MedicationOption {
  key: string;
  label_en: string;
  drug_class: string;
  brand_examples: string[];
}

export interface MedicalEntry {
  id: string;
  profile_id: string;
  condition_key?: string | null;
  medication_key?: string | null;
  custom_label: string | null;
  label_en: string | null;
  note: string | null;
  started_on: string | null;
  ends_on: string | null;
  is_time_boxed?: boolean;
  summary_en?: string | null;
  drug_class?: string | null;
}

export interface MedicalState {
  enabled: boolean;
  today?: string;
  people: Array<{
    id: string;
    name: string;
    conditions: MedicalEntry[];
    medications: MedicalEntry[];
  }>;
  /**
   * What the app will actually DO about all this.
   *
   * Shown in the editor rather than left implicit: a household that records a
   * diagnosis deserves to see the effect, not to trust that something happens
   * somewhere. It is also the honest place to show that a free-text entry has
   * no rules behind it.
   */
  effect?: {
    prefer: string[];
    rules: Array<{
      source: "condition" | "medication" | "amplifier";
      title: string;
      severity: "warn" | "advisory";
      guidance: string;
      people: string[];
      sourceNote: string;
    }>;
    customEntries: string[];
  };
  disclaimer: string;
}

export const medicalApi = {
  catalog: () =>
    api.get<{
      conditions: MedicalConditionOption[];
      medications: MedicationOption[];
      disclaimer: string;
    }>("/api/medical/catalog"),

  get: () => api.get<MedicalState>("/api/medical"),

  addCondition: (profileId: string, body: Record<string, unknown>) =>
    api.post<MedicalEntry>(`/api/medical/profiles/${profileId}/conditions`, body),
  updateCondition: (id: string, body: Record<string, unknown>) =>
    api.patch<MedicalEntry>(`/api/medical/conditions/${id}`, body),
  removeCondition: (id: string) => api.del<{ ok: true }>(`/api/medical/conditions/${id}`),

  addMedication: (profileId: string, body: Record<string, unknown>) =>
    api.post<MedicalEntry>(`/api/medical/profiles/${profileId}/medications`, body),
  updateMedication: (id: string, body: Record<string, unknown>) =>
    api.patch<MedicalEntry>(`/api/medical/medications/${id}`, body),
  removeMedication: (id: string) => api.del<{ ok: true }>(`/api/medical/medications/${id}`),
};

export interface ChefConversationSummary {
  id: string;
  title: string;
  lastMessageAt: string;
  createdAt: string;
  messageCount: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Endpoints
// ─────────────────────────────────────────────────────────────────────────────

export const chefApi = {
  listConversations: () =>
    api.get<{ conversations: ChefConversationSummary[] }>("/api/chef/conversations"),

  newConversation: () =>
    api.post<ChefConversation>("/api/chef/conversations"),

  getConversation: (id: string) =>
    api.get<ChefConversation>(`/api/chef/conversations/${id}`),

  archiveConversation: (id: string) =>
    api.del<{ ok: true }>(`/api/chef/conversations/${id}`),

  send: (text: string, conversationId: string | null) =>
    api.post<{
      conversationId: string;
      userMessage: ChefMessage;
      message: ChefMessage;
    }>("/api/chef/message", { text, conversationId }),

  applyProposal: (id: string) =>
    api.post<{ ok: true; kind: string; result: Record<string, unknown> }>(
      `/api/chef/proposals/${id}/apply`),

  dismissProposal: (id: string) =>
    api.post<{ ok: true }>(`/api/chef/proposals/${id}/dismiss`),

  saveDraft: (id: string) =>
    api.post<{ recipeId: string; title: string }>(`/api/chef/drafts/${id}/save`),

  /** PDF of an unsaved draft. */
  downloadDraftPdf: (id: string, title: string) =>
    api.download(`/api/chef/drafts/${id}/export.pdf`, `${slug(title)}.pdf`),

  /** PDF of a saved recipe, optionally rescaled for printing. */
  downloadRecipePdf: (id: string, title: string, servings?: number) =>
    api.download(
      `/api/recipes/${id}/export.pdf${servings ? `?servings=${servings}` : ""}`,
      `${slug(title)}.pdf`),
};

function slug(title: string): string {
  return title.toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "recipe";
}
